/**
 * Flat transaction view for the Expenses page — renders every fetched expense
 * as its own row so the 10/25/50 page size maps 1:1 to transactions (not days).
 * The daily-grouped view remains available as an optional view.
 */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trash2, AlertTriangle } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';

export function ExpensesFlatTable({ expenses, branches, safeFormat, onDelete }) {
  const rows = (expenses || []).slice().sort((a, b) => new Date(b.date) - new Date(a.date));
  if (rows.length === 0) return null;

  return (
    <div data-testid="expenses-flat-table" className="border rounded-xl overflow-hidden">
      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 sticky top-0 z-10">
            <tr className="border-b">
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Date</th>
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Branch</th>
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Category</th>
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Supplier / Description</th>
              <th className="px-3 py-2.5 text-right font-medium text-muted-foreground">Amount</th>
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Payment</th>
              <th className="px-3 py-2.5 text-right font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.id} className="border-b hover:bg-muted/30" data-testid={`expense-flat-row-${e.id}`}>
                <td className="px-3 py-2 whitespace-nowrap text-xs">{safeFormat(e.date, 'MMM dd, yyyy')}</td>
                <td className="px-3 py-2 text-xs">
                  <span>{e.owner_branch_name || branches.find((b) => b.id === e.branch_id)?.name || '-'}</span>
                  {e.is_cross_branch && (
                    <span
                      className="ml-1 inline-flex items-center rounded bg-info/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-info"
                      title={`Paid by ${e.paid_by_branch_name} on behalf of ${e.owner_branch_name}`}
                      data-testid={`expense-paidby-${e.id}`}
                    >
                      Paid by {e.paid_by_branch_name}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <Badge variant="secondary" className="capitalize text-[10px]">{e.category?.replace('_', ' ')}</Badge>
                  {e.sub_category && <Badge variant="outline" className="ml-1 text-[10px] capitalize">{e.sub_category}</Badge>}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[240px]">{e.supplier_name || e.paid_to || e.description || '-'}</td>
                <td className="px-3 py-2 text-right font-bold text-error whitespace-nowrap"><span className="inline-flex items-center gap-0.5"><RiyalIcon size="0.7em" /> {Number(e.amount || 0).toFixed(2)}</span></td>
                <td className="px-3 py-2">
                  <Badge className={`capitalize text-[10px] ${e.payment_mode === 'cash' ? 'bg-cash/20 text-cash' : e.payment_mode === 'bank' ? 'bg-bank/20 text-bank' : 'bg-credit/20 text-credit'}`}>{e.payment_mode}</Badge>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="outline" onClick={() => onDelete(e.id)} className="h-7 px-2 text-error hover:text-error" data-testid={`expense-flat-delete-${e.id}`}>
                    <Trash2 size={12} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2.5 text-xs text-muted-foreground bg-muted/50 border-t flex items-center gap-1.5">
        <AlertTriangle size={11} className="text-muted-foreground/60" />
        Showing {rows.length.toLocaleString()} transaction{rows.length === 1 ? '' : 's'} on this page — switch to Daily View to group by day.
      </div>
    </div>
  );
}

export default ExpensesFlatTable;
